import { useState } from 'react';
import { useSchedule } from '../hooks/useSchedule';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useOffices } from '../hooks/useOffices';
import { useServiceLines } from '../hooks/useServiceLines';
import { ScheduleGrid } from '../components/schedule/ScheduleGrid';
import { ScheduleFilters, type Density } from '../components/schedule/ScheduleFilters';
import { ScheduleDetailPanel } from '../components/schedule/ScheduleDetailPanel';
import { StaleScheduleNotice } from '../components/schedule/StaleScheduleNotice';
import { SavedFiltersMenu } from '../components/SavedFiltersMenu';
import { useScheduleFilterState } from '../hooks/useScheduleFilterState';
import { useIsMobile } from '../hooks/useIsMobile';
import { ScheduleMobilePage } from './mobile/ScheduleMobilePage';
import type { ScheduleCellAssignment } from '../lib/types';

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateStr(d: Date) {
  return d.toISOString().slice(0, 10);
}

function densityToVisibleDays(d: Density): number {
  // Number of day-columns visible at once for each density.
  // Day = 1 (full width), Week = 7 (1/7 per column), Month = 30 (1/7 per column,
  // horizontal scroll for the remaining days). ScheduleGrid sizes columns based on
  // its measured container width + this count.
  return d === 'day' ? 1 : d === 'week' ? 7 : 30;
}

/**
 * Phones get a different page, not a squeezed grid — see {@link ScheduleMobilePage}. The
 * branch is a whole component swap so the desktop grid's queries and measurement effects
 * never run on a phone (and vice versa); rotating a tablet past 768px switches live.
 */
export function SchedulePage() {
  return useIsMobile() ? <ScheduleMobilePage /> : <ScheduleDesktopPage />;
}

function ScheduleDesktopPage() {
  const { data: currentUser } = useCurrentUser();

  const [density, setDensity] = useState<Density>('week');
  const [windowStart, setWindowStart] = useState(() => new Date());
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds } =
    useScheduleFilterState();
  const { data: offices } = useOffices();
  const { data: serviceLines } = useServiceLines();

  const visibleDays = densityToVisibleDays(density);
  const from = toDateStr(windowStart);
  // Inclusive range: [windowStart, windowStart + visibleDays - 1].
  const to = toDateStr(addDays(windowStart, visibleDays - 1));

  const scheduleQuery = useSchedule({
    from,
    to,
    region: filters.region || undefined,
    office: filters.office || undefined,
    serviceLine: filters.serviceLine || undefined,
    personIds,
    projectIds,
  });

  const isLoading = scheduleQuery.isLoading;
  const data = scheduleQuery.data;
  const error = scheduleQuery.error;

  const rows = data?.rows ?? [];
  const days = data?.days ?? [];

  const regions = Array.from(new Set(rows.map((r) => r.region).filter(Boolean) as string[]));

  function handleToday() {
    setWindowStart(new Date());
  }

  function handlePrev() {
    setWindowStart((prev) => addDays(prev, -densityToVisibleDays(density)));
  }

  function handleNext() {
    setWindowStart((prev) => addDays(prev, densityToVisibleDays(density)));
  }

  function handleWindowStartChange(d: string) {
    // Parse as local-date midnight to avoid TZ surprises on the date picker
    setWindowStart(new Date(d + 'T00:00:00'));
  }

  function handleCellClick(assignment: ScheduleCellAssignment) {
    setSelectedJob(assignment.jobNumber);
  }

  // flex-1, not h-full: the parent <main> is a flex item, so percentage heights
  // resolve to auto and h-full collapses the page to its content — leaving a large
  // empty band above the app footer. Growing via flex fills the viewport instead.
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <ScheduleFilters
        regions={regions}
        offices={(offices ?? []).map((o) => o.name)}
        serviceLines={(serviceLines ?? []).map((s) => s.name)}
        value={filters}
        onChange={changeFilters}
        density={density}
        onDensityChange={setDensity}
        onTodayClick={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        windowStart={from}
        onWindowStartChange={handleWindowStartChange}
        filterMenu={
          <SavedFiltersMenu
            applied={appliedFilter}
            onApply={applySavedFilter}
            currentPeople={filters.people}
            currentProjects={filters.projects}
          />
        }
      />

      <StaleScheduleNotice />
      {isLoading && (
        <div className="flex-1 p-4">
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-1 animate-pulse">
                <div className="w-48 h-10 bg-slate-100 rounded" />
                {Array.from({ length: 20 }).map((__, j) => (
                  <div key={j} className="w-[52px] h-10 bg-slate-100 rounded" />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="m-4 bg-red-50 border border-red-200 rounded p-4 text-sm text-red-900">
          Could not load the schedule. Try refreshing the page.
          <button
            type="button"
            onClick={() => scheduleQuery.refetch()}
            className="ml-3 text-brand-blue hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="m-4 text-sm text-slate-500">
          {currentUser?.isAuthenticated
            ? 'No assignments found for the selected filters. Try clearing a filter or adjusting the date range.'
            : 'Your account is not linked to a crew member profile. Ask your admin to verify your email matches your name in the schedule source.'}
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <ScheduleGrid
          rows={rows}
          days={days}
          density={density}
          onCellClick={handleCellClick}
        />
      )}

      <ScheduleDetailPanel
        jobNumber={selectedJob}
        onClose={() => setSelectedJob(null)}
      />
    </div>
  );
}
